import { Row, Button, Table } from "antd";
import { ExperimentOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import PageHeader from "../components/generic/PageHeader";
import { useLocation } from "react-router-dom";
import {
  getTestCaseDetail,
  getTestCaseSummary,
} from "../service/suite.service";
import { arrayFilter } from "../helper/array_helper";
import "../styles/table.css";
import RunTaskModal from "../components/modal/RunTaskModal";
import { useNavigate } from "react-router-dom";

function TestCase() {
  const location = useLocation();
  const navigate = useNavigate();
  // Optional-chained: this page is reached by navigating from the Test Suite
  // library, which passes the suite in location.state. On a refresh, a direct
  // URL hit, or a project switch there is no state — reading it unguarded threw
  // a TypeError and blanked the app.
  const suiteId = location.state?.key;
  const suiteName = location.state?.name;
  const specName = location.state?.specName;
  const [nestedData, setNestedData] = useState([]);
  const [testCaseSummary, setTestCaseSummary] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // Nothing to show without a suite — send them back to the library.
  useEffect(() => {
    if (!suiteId) navigate("/libraries/suites", { replace: true });
  }, [suiteId, navigate]);

  useEffect(() => {
    if (!suiteId) return;
    getTestCaseSummary(suiteId).then((summaryResponse) => {
      if (summaryResponse.status === 200) {
        setTestCaseSummary(summaryResponse.data);
        getTestCaseDetail(suiteId).then((detailResponse) => {
          setNestedData(detailResponse.data);
        });
      }
    });
  }, [suiteId]);

  const openModal = () => {
    setIsOpen(!isOpen);
  };

  const expandedRowRender = (record) => {
    const columns = [
      {
        title: "Column",
        className: "nested-table",
        dataIndex: "columnName",
        key: "columnName",
        width: "40%",
      },
      {
        title: "Value",
        className: "nested-table",
        dataIndex: "columnValue",
        key: "columnValue",
      },
    ];
    let data = [];
    switch (record.testName) {
      case "Field Length":
        data = arrayFilter(nestedData, "Field Length");
        break;
      case "Not Null Check":
        data = arrayFilter(nestedData, "Not Null Check");
        break;
      case "Mandatory Column":
        data = arrayFilter(nestedData, "Mandatory Column");
        break;
      case "Date Time Format Validation":
        data = arrayFilter(nestedData, "Date Time Format Validation");
        break;
      case "Data Type":
        data = arrayFilter(nestedData, "Data Type");
        break;
      case "Case":
        data = arrayFilter(nestedData, "Case");
        break;
      case "Allowable Values":
        data = arrayFilter(nestedData, "Allowable Values");
        break;
      case "Max File Size":
        data = arrayFilter(nestedData, "Max File Size");
        break;
      case "File Type":
        data = arrayFilter(nestedData, "File Type");
        break;
      case "Header Row Validation":
        data = arrayFilter(nestedData, "Header Row Validation");
        break;
      case "Within Compressed Folder (e.g. zip, gz)":
        data = arrayFilter(
          nestedData,
          "Within Compressed Folder (e.g. zip, gz)",
        );
        break;
      case "Duplicate Data Validation":
        data = arrayFilter(nestedData, "Duplicate Data Validation");
        break;
      case "Field Positions Validation":
        data = arrayFilter(nestedData, "Field Positions Validation");
        break;
      case "Field Level":
        data = arrayFilter(nestedData, "Field Level");
        break;
      default:
        break;
    }
    return (
      <Table
        className="nested-table"
        columns={columns}
        dataSource={data}
        size="small"
        bordered
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
        }}
      />
    );
  };

  const columns = [
    {
      title: "Test Type",
      dataIndex: "testType",
      key: "testType",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.testType.localeCompare(b.testType),
    },
    {
      title: "Test Name",
      dataIndex: "testName",
      key: "testName",
    },
    {
      title: "Test Count",
      dataIndex: "totalCount",
      key: "totalCount",
    },
  ];

  // The redirect above is in flight — render nothing rather than a broken shell.
  if (!suiteId) return null;

  return (
    <div className="db-container">
      <PageHeader
        onBack={() => navigate("/libraries/suites")}
        icon={<ExperimentOutlined />}
        title={`Test Suite ${suiteName}`}
        actions={
          <>
            <Button className="db-add">Create Test case</Button>
            <Button className="db-add" onClick={openModal}>
              Run Suite
            </Button>
          </>
        }
      />
      <Row className="table-container">
        <Table
          className="components-table-demo-nested"
          columns={columns}
          expandable={{
            rowExpandable: (record) =>
              record.testType === "Column Level" ||
              record.testType === "Field Level",
            expandedRowRender: expandedRowRender,
          }}
          dataSource={testCaseSummary}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30"],
          }}
        />
      </Row>
      <RunTaskModal
        isOpen={isOpen}
        openModal={openModal}
        suiteName={suiteName}
        specName={specName}
        suiteId={suiteId}
      />
    </div>
  );
}

export default TestCase;
